import React from 'react';
import { TIMER_STATE } from './lessonTimerModel';
import './TimerPill.css';

/**
 * The timer pill — topbar, never a card. Separated by numerals + icon; hue
 * reinforces on icon/border only; the label stays --text-body ≥9:1 in every
 * theme (handoff §Timer). Overrun is informational and never blocks.
 *
 * SINCE 2026-09-02 THE PILL COUNTS DOWN to the scheduled end rather than up
 * from a button press, so the label is time LEFT, and the pill is tappable
 * whenever the actual start has not been recorded yet — including mid-lesson,
 * which is how a teacher "enters a late start" without a time picker: one tap
 * says *it is starting now*, and the model turns that into the running-behind
 * figure.
 *
 * The `note` is ALWAYS RENDERED when the model supplies one, never revealed on
 * hover: the portal's primary user has keratoconus, and the installed PWA has
 * no hover at all (CLAUDE.md). It is quieted with a token, not with opacity.
 */
const PILL = {
	[TIMER_STATE.UPCOMING]: { icon: 'fa-clock', hue: 'idle' },
	[TIMER_STATE.IDLE]: { icon: 'fa-play', hue: 'idle' },
	[TIMER_STATE.RUNNING]: { icon: 'fa-hourglass-half', hue: 'success' },
	[TIMER_STATE.ENDING_SOON]: { icon: 'fa-hourglass-end', hue: 'warning' },
	[TIMER_STATE.OVERRUN]: { icon: 'fa-triangle-exclamation', hue: 'danger' },
	[TIMER_STATE.SAVED]: { icon: 'fa-circle-check', hue: 'muted' },
	[TIMER_STATE.PAST]: { icon: 'fa-calendar-check', hue: 'muted' },
};

/** A past lesson with no attendance is the one thing on the pill that still
 *  needs a person, so it keeps the warning hue. */
const PAST_UNMARKED = { icon: 'fa-circle-exclamation', hue: 'warning' };

const TimerPill = ({ timer, onStart }) => {
	const isPast = timer.state === TIMER_STATE.PAST;
	const spec =
		isPast && timer.needsAttendance
			? PAST_UNMARKED
			: PILL[timer.state] || PILL[TIMER_STATE.IDLE];
	const isSaved = timer.state === TIMER_STATE.SAVED;
	// Tappable until the actual start is on the record. `lateStartMs` is only
	// meaningful once started, so absence of a start is what we key on — and the
	// model reports that through the note, not through a boolean, so read the
	// same signal it does.
	// A PAST lesson is never tappable: "it is starting now" is false about a
	// lesson that ended an hour or days ago, and the tap would write it.
	const notStarted = !isSaved && !isPast && !timer.startedRecorded;

	const inner = (
		<>
			<i className={`fa-sharp fa-solid ${spec.icon}`} aria-hidden="true" />
			<span className="ld-timer__label">{timer.label}</span>
		</>
	);

	const pill = notStarted ? (
		<button
			type="button"
			className={`ld-timer ld-timer--${spec.hue} ld-timer--tappable`}
			onClick={onStart}
			title="Record that the lesson is starting now">
			{inner}
		</button>
	) : (
		<span className={`ld-timer ld-timer--${spec.hue}`} role="timer" aria-live="off">
			{inner}
		</span>
	);

	if (!timer.note) return pill;

	return (
		<span className="ld-timerwrap">
			{pill}
			<span className="ld-timer__note">{timer.note}</span>
		</span>
	);
};

export default TimerPill;
